<script>
import { apiSecond } from '@/boot/axios'

import AqEmpty from '@/components/aqua/AqEmpty.vue'
import AqPageHeader from '@/components/aqua/AqPageHeader.vue'

import { OBLIGATION_STATUSES, PERCENT_KEYS, aquaApiPrefix } from './aquaForms'
import AquaSelect from './AquaSelect.vue'

const SUM_COLUMNS = {
	1: ['produced_volume_ton', 'harvested_volume_ton', 'sold_volume_ton', 'sold_amount_kzt', 'production_capacity_ton', 'planned_production_ton', 'actual_production_ton', 'feed_cost_kzt', 'investment_cost_kzt'],
	2: ['feed_cost_previous_period_kzt', 'domestic_feed_volume_ton', 'imported_feed_volume_ton', 'domestic_feed_used_volume_ton', 'imported_feed_used_volume_ton', 'feed_requirement_next_year_ton'],
	3: ['extruded_feed_volume_ton', 'specialized_feed_volume_ton', 'investment_amount_kzt', 'enterprise_capacity_ton', 'planned_production_volume_ton', 'actual_production_volume_ton'],
	4: ['purchased_breeding_stock_qty_prev_period', 'breeding_stock_purchase_cost_kzt', 'breeding_stock_total_qty', 'breeding_stock_quality_evaluated_qty', 'breeding_stock_genomic_evaluated_qty'],
	5: OBLIGATION_STATUSES.map((status) => `obligations_${status}`),
	6: ['fish_production_plan_ton', 'fish_production_fact_ton', 'mto_plan_kzt', 'mto_fact_kzt', 'construction_plan_kzt', 'construction_fact_kzt', 'stocking_plan_qty', 'stocking_fact_qty'],
}

export default {
	name: 'AquaReportSummary',
	components: {
		AqEmpty,
		AqPageHeader,
		AquaSelect,
	},
	props: {
		scope: {
			type: String,
			default: 'go',
		},
	},
	data() {
		return {
			rows: [],
			total: null,
			loading: false,
			reportYear: new Date().getFullYear() - 1,
			overflowRight: false,
		}
	},
	computed: {
		form() {
			return Number(this.$route.params.form)
		},
		years() {
			const current = new Date().getFullYear()
			return Array.from({ length: 7 }, (_, index) => current - index)
		},
		yearOptions() {
			return this.years.map((year) => ({ value: year, text: year }))
		},
		listPath() {
			const base = this.scope === 'go' ? 'reporting-analytics/aqua' : 'reporting/aqua-tir'
			return `/${this.$i18n.locale}/account/${base}/${this.form}`
		},
		columns() {
			const sums = SUM_COLUMNS[this.form] || []
			const percents = PERCENT_KEYS.filter((key) => this.rows.length && key in this.rows[0])
			return [...sums, ...percents]
		},
		headers() {
			return [
				{ text: this.$t('inputs.region'), value: 'region_title', sortable: false },
				{ text: this.$t('aqua.reports_count'), value: 'reports_count', sortable: false, align: 'end' },
				...this.columns.map((key) => ({ text: this.columnLabel(key), value: key, sortable: false, align: 'end' })),
			]
		},
	},
	watch: {
		reportYear() {
			this.fetch()
		},
		rows() {
			this.$nextTick(this.updateOverflow)
		},
	},
	mounted() {
		const wrapper = this.tableWrapper()
		if (wrapper) wrapper.addEventListener('scroll', this.updateOverflow, { passive: true })
		window.addEventListener('resize', this.updateOverflow)
	},
	beforeDestroy() {
		const wrapper = this.tableWrapper()
		if (wrapper) wrapper.removeEventListener('scroll', this.updateOverflow)
		window.removeEventListener('resize', this.updateOverflow)
	},
	methods: {
		tableWrapper() {
			return this.$el.querySelector('.su-dt .v-data-table__wrapper')
		},
		updateOverflow() {
			const wrapper = this.tableWrapper()
			this.overflowRight = Boolean(wrapper) && wrapper.scrollLeft + wrapper.clientWidth < wrapper.scrollWidth - 1
		},
		columnLabel(key) {
			if (key.startsWith('obligations_')) return this.$t(`aqua.obligation_status.${key.replace('obligations_', '')}`)
			if (PERCENT_KEYS.includes(key)) return this.$t(`aqua.percents.${key}`)
			return this.$t(`aqua.fields.${key}`)
		},
		format(key, value) {
			if (value === null || value === undefined) return '—'
			return PERCENT_KEYS.includes(key) ? `${String(value).replace('.', ',')} %` : new Intl.NumberFormat('ru-RU').format(value)
		},
		async fetch() {
			this.loading = true
			try {
				const { data } = await apiSecond.get(`${aquaApiPrefix(this.scope, this.form)}/summary`, {
					params: { report_year: this.reportYear },
				})
				this.rows = data.data
				this.total = data.total
			} catch (error) {
				if (error?.response?.status === 401) this.$router.push(`/${this.$i18n.locale}/login`)
				else this.$toast.error(error?.response?.data?.message || this.$t('system_message.500'))
			} finally {
				this.loading = false
			}
		},
	},
	beforeCreate() {
		if (!localStorage.token) this.$router.push('/ru/login')
	},
	beforeMount() {
		this.fetch()
	},
}
</script>

<template>
	<div class="wrap__content aq-page">
		<div class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="[scope === 'go' ? 'ReportingAnalyticManagement' : 'ReportingManagement']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content aq-shell">
							<AqPageHeader
								:title="$t('aqua.ui.summary.title')"
								:eyebrow="`${form}-аква`"
								:subtitle="$t(`aqua.forms.${form}`)"
								:back="listPath"
								:back-label="$t('aqua.ui.to_list')"
							>
								<template v-if="rows.length && total" #meta>
									<span class="aq-badge">{{ $t('aqua.ui.summary.reports', { count: total.reports_count }) }}</span>
									<span class="aq-badge aq-badge--neutral">{{ $t('aqua.ui.summary.regions', { count: rows.length }) }}</span>
								</template>
								<template #actions>
									<AquaSelect v-model="reportYear" class="su-year" :label="$t('aqua.ui.summary.year')" :options="yearOptions" />
								</template>
							</AqPageHeader>

							<p v-if="overflowRight" class="su-scroll-hint">
								{{ $t('aqua.ui.summary.scroll_hint') }}
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
									<path d="M5 12h14M13 6l6 6-6 6" />
								</svg>
							</p>
							<section class="aq-card su-card" :class="{ 'su-card--overflow': overflowRight }">
								<v-data-table
									class="su-dt"
									:headers="headers"
									:items="rows"
									:loading="loading"
									loader-height="3"
									hide-default-footer
									disable-pagination
								>
									<template v-slot:item.region_title="{ item }">
										<strong class="su-region">{{ item.region_title }}</strong>
									</template>
									<template v-for="key in columns" v-slot:[`item.${key}`]="{ item }">
										<span :key="key" :class="{ 'su-empty': item[key] === null || item[key] === undefined }">{{ format(key, item[key]) }}</span>
									</template>
									<template v-if="total && rows.length" v-slot:body.append>
										<tr class="su-total">
											<td>{{ $t('aqua.total') }}</td>
											<td class="text-end">{{ format('reports_count', total.reports_count) }}</td>
											<td v-for="key in columns" :key="key" class="text-end">{{ format(key, total[key]) }}</td>
										</tr>
									</template>
									<template v-slot:no-data>
										<AqEmpty :title="$t('aqua.ui.summary.empty_title')" :text="$t('aqua.ui.summary.empty_text', { year: reportYear })" />
									</template>
								</v-data-table>
							</section>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.aq-shell {
	background: var(--aq-bg);
}
.su-year {
	width: 160px;
}
.su-year ::v-deep .aqua-select__label {
	color: var(--aq-ink-2);
	font-weight: 500;
}
.su-year ::v-deep .aqua-select__control {
	height: 44px;
	border-color: var(--aq-border-strong);
	border-radius: 10px;
}
.su-year ::v-deep .aqua-select__control:hover {
	border-color: var(--aq-primary);
}
.su-card {
	position: relative;
	overflow: hidden;
}
.su-card--overflow::after {
	content: '';
	position: absolute;
	top: 0;
	right: 0;
	bottom: 12px;
	width: 48px;
	z-index: 4;
	background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.95));
	pointer-events: none;
}
.su-scroll-hint {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 6px;
	margin: 0 0 8px;
	color: var(--aq-muted);
	font-size: 13px;
}
.su-dt ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
	height: 10px;
}
.su-dt ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
	border-radius: 999px;
	background: var(--aq-border-strong);
}
.su-dt ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
	background: #f3f6fa;
}
.su-region {
	font-weight: 600;
}
.su-empty {
	color: #b6c3d3;
}
.su-dt ::v-deep .v-data-table__wrapper {
	max-height: calc(100vh - 220px);
	overflow: auto;
}
.su-dt ::v-deep table {
	font-variant-numeric: tabular-nums;
}
.su-dt ::v-deep .text-end {
	text-align: right !important;
}
.su-dt ::v-deep thead th {
	position: sticky;
	top: 0;
	z-index: 2;
	min-width: 120px;
	max-width: 190px;
	height: auto !important;
	padding: 12px 14px !important;
	background: #f8fafd;
	color: var(--aq-ink-2) !important;
	font-size: 12px !important;
	font-weight: 600;
	line-height: 1.35;
	white-space: normal !important;
	vertical-align: bottom;
	box-shadow: inset 0 -1px 0 var(--aq-border);
}
.su-dt ::v-deep thead th span {
	white-space: normal !important;
}
.su-dt ::v-deep tbody td {
	height: 52px;
	padding: 8px 14px !important;
	border-bottom: 1px solid var(--aq-border) !important;
	color: var(--aq-ink);
	font-size: 14px !important;
	white-space: nowrap;
}
.su-dt ::v-deep thead th:first-child,
.su-dt ::v-deep tbody td:first-child {
	position: sticky;
	left: 0;
	min-width: 220px;
	padding-left: 24px !important;
	white-space: normal;
	box-shadow: inset -1px 0 0 var(--aq-border);
}
.su-dt ::v-deep thead th:first-child {
	z-index: 3;
	box-shadow: inset -1px -1px 0 var(--aq-border);
}
.su-dt ::v-deep tbody td:first-child {
	z-index: 1;
	background: var(--aq-surface);
}
.su-dt ::v-deep tbody tr:hover:not(.su-total):not(.v-data-table__empty-wrapper),
.su-dt ::v-deep tbody tr:hover:not(.su-total):not(.v-data-table__empty-wrapper) td:first-child {
	background: #f8fbff !important;
}
.su-dt ::v-deep .v-data-table__empty-wrapper td {
	height: auto;
}
.su-dt ::v-deep .su-total td {
	position: sticky;
	bottom: 0;
	border-top: 2px solid var(--aq-border-strong);
	border-bottom: 0 !important;
	background: var(--aq-primary-soft) !important;
	color: var(--aq-ink);
	font-weight: 700;
}
.su-dt ::v-deep .su-total td:first-child {
	z-index: 2;
}
</style>
